import type {
  NewHomeSection,
  NewHomeSectionValue,
  NewHomePageData,
  FloatingNavConfig,
  FloatingNavChrome,
  MobileHeaderConfig,
  DesktopHeaderConfig,
} from '../../domain/models/NewHomePageData';
import type {
  NewHomeSectionDTO,
  NewHomePageDataDTO,
  FloatingNavConfigDTO,
  FloatingNavChromeDTO,
  MobileHeaderConfigDTO,
  DesktopHeaderConfigDTO,
} from '../dto/newHome.dto';
import { DEFAULT_DESKTOP_HEADER } from '@/app/store/headerConfigSlice';

/**
 * Mapper: DTO → Domain
 * Converts raw JSON/API sections to typed domain models
 */
export function adaptNewHomeSectionDtoToDomain(dto: NewHomeSectionDTO): NewHomeSection {
  const value = dto.value as unknown as NewHomeSectionValue;
  return {
    priority: dto.priority,
    key: dto.key,
    showInMobile: dto.showInMobile,
    showInDesktop: dto.showInDesktop,
    eventLabel: dto.eventLabel,
    value,
  };
}

const DEFAULT_FLOATING_NAV_CHROME: FloatingNavChrome = {
  glassBackground: 'rgba(255,255,255,0.8)',
  glassPillBorderOpen: '1px solid rgba(0,0,0,0.10)',
  glassBarBorder: '1px solid rgba(0,0,0,0.08)',
  useLightIcons: true,
};

function adaptFloatingNavChrome(dto: FloatingNavChromeDTO | undefined): FloatingNavChrome {
  if (!dto) {
    return { ...DEFAULT_FLOATING_NAV_CHROME };
  }
  return {
    glassBackground: dto.glassBackground ?? DEFAULT_FLOATING_NAV_CHROME.glassBackground,
    glassPillBorderOpen: dto.glassPillBorderOpen ?? DEFAULT_FLOATING_NAV_CHROME.glassPillBorderOpen,
    glassBarBorder: dto.glassBarBorder ?? DEFAULT_FLOATING_NAV_CHROME.glassBarBorder,
    useLightIcons: dto.useLightIcons ?? DEFAULT_FLOATING_NAV_CHROME.useLightIcons,
  };
}

/** Slow flight + ease-out at end so the mark settles gently on the FAB. */
const DEFAULT_LOGO_FLY_MS = 1400;
/** Pause after landing before the floating pill collapses into the BS control. */
const DEFAULT_MERGE_DELAY_SEC = 2;
/** Strong deceleration over the last segment of the path. */
const DEFAULT_LOGO_FLY_EASING = 'cubic-bezier(0.06, 0.82, 0.14, 1)';
/** Glass pill width/opacity collapse toward the FAB. */
const DEFAULT_MERGE_TRANSITION_MS = 720;

export function adaptFloatingNavDtoToDomain(dto: FloatingNavConfigDTO): FloatingNavConfig {
  return {
    enabled: dto.enabled,
    collapseAtSectionName: dto.collapseAtSectionName ?? '',
    expandAtSectionName: dto.expandAtSectionName ?? '',
    showInMobile: dto.showInMobile,
    showInDesktop: dto.showInDesktop,
    logoUrl: dto.logoUrl,
    states: dto.states,
    transition: dto.transition,
    chrome: adaptFloatingNavChrome(dto.chrome),
    logoFlyDurationMs: dto.logoFlyDurationMs ?? DEFAULT_LOGO_FLY_MS,
    mergeDelayAfterLogoLandsSec: dto.mergeDelayAfterLogoLandsSec ?? DEFAULT_MERGE_DELAY_SEC,
    logoFlyEasing: dto.logoFlyEasing ?? DEFAULT_LOGO_FLY_EASING,
    mergeTransitionMs: dto.mergeTransitionMs ?? DEFAULT_MERGE_TRANSITION_MS,
    cartShouldRefresh: dto.cartShouldRefresh ?? false,
    profileShouldRefresh: dto.profileShouldRefresh ?? false,
  };
}

export function adaptMobileHeaderDtoToDomain(dto: MobileHeaderConfigDTO): MobileHeaderConfig {
  return { bgColor: dto.bgColor, logoColor: dto.logoColor };
}

export function adaptDesktopHeaderDtoToDomain(dto: DesktopHeaderConfigDTO): DesktopHeaderConfig {
  return {
    topBarBg: dto.topBarBg ?? DEFAULT_DESKTOP_HEADER.topBarBg,
    mainBarBg: dto.mainBarBg ?? DEFAULT_DESKTOP_HEADER.mainBarBg,
    headerBottomBorderColor: dto.headerBottomBorderColor ?? DEFAULT_DESKTOP_HEADER.headerBottomBorderColor,
    headerTextColor: dto.headerTextColor ?? DEFAULT_DESKTOP_HEADER.headerTextColor,
    navbarBg: dto.navbarBg ?? DEFAULT_DESKTOP_HEADER.navbarBg,
    navbarLinkColor: dto.navbarLinkColor ?? DEFAULT_DESKTOP_HEADER.navbarLinkColor,
    searchBg: dto.searchBg ?? DEFAULT_DESKTOP_HEADER.searchBg,
    searchBorderColor: dto.searchBorderColor ?? DEFAULT_DESKTOP_HEADER.searchBorderColor,
    searchTextColor: dto.searchTextColor ?? DEFAULT_DESKTOP_HEADER.searchTextColor,
    searchPlaceholderColor: dto.searchPlaceholderColor ?? DEFAULT_DESKTOP_HEADER.searchPlaceholderColor,
    searchIconColor: dto.searchIconColor ?? DEFAULT_DESKTOP_HEADER.searchIconColor,
    layout: dto.layout ?? DEFAULT_DESKTOP_HEADER.layout,
  };
}

export function adaptNewHomePageDataDtoToDomain(dto: NewHomePageDataDTO): NewHomePageData {
  return {
    mobileHeader: dto.mobileHeader ? adaptMobileHeaderDtoToDomain(dto.mobileHeader) : undefined,
    desktopHeader: dto.desktopHeader ? adaptDesktopHeaderDtoToDomain(dto.desktopHeader) : undefined,
    floatingNav: adaptFloatingNavDtoToDomain(dto.floatingNav),
    sections: dto.sections.map(adaptNewHomeSectionDtoToDomain),
    ...(dto.homeSeoContentSection != null
      ? { homeSeoContentSection: dto.homeSeoContentSection }
      : {}),
  };
}
